import { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { getTopProductSales } from "../services/SaleService";
import { useTheme, useMediaQuery } from "@mui/material";
import { tokens } from "../theme";

const InventoryOut = ({ isDashboard = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);

  useEffect(() => {
    getTopProductSales(5)
      .then((resp) => {
        const payload = resp?.data ?? [];

        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];

        const mapped = items.map((it) => {
          const title =
            it?.title ??
            it?.name ??
            (it?.productId ? `Product ${it.productId}` : "Unknown");
          const units = Number(
            it?.unitsSold ?? it?.totalUnits ?? it?.units ?? 0,
          );
          return {
            category: title,
            stock: isNaN(units) ? 0 : units,
          };
        });

        const totalAll = Number(
          payload?.totalUnitsAll ?? payload?.totalUnitsAllValue ?? NaN,
        );
        if (!isNaN(totalAll)) {
          const sumTop = mapped.reduce((s, m) => s + (Number(m.stock) || 0), 0);
          const other = Math.max(0, totalAll - sumTop);
          if (other > 0) {
            mapped.push({ category: "Other", stock: other });
          }
        }

        setData(mapped);
      })
      .catch((error) => {
        console.error("Error fetching top product sales:", error);
        setData([]);
      });
  }, []);

  const getColor = (stock) => {
    if (stock <= 20) return colors.redAccent[600];
    if (stock > 20 && stock <= 100) return colors.yellowAccent[800];
    return colors.greenAccent[600];
  };

  // Dynamically allocate more bottom room on mobile for the slanted labels
  // Reduced overall margins for dashboard to let the chart expand
  const chartMargin = isDashboard
    ? { top: 20, right: 20, bottom: isMobile ? 60 : 30, left: 40 }
    : { top: 50, right: 130, bottom: 130, left: 90 };

  return (
    <ResponsiveBar
      data={data}
      theme={{
        axis: {
          domain: { line: { stroke: colors.gray[100] } },
          legend: { text: { fill: colors.gray[100] } },
          ticks: {
            line: { stroke: colors.gray[100], strokeWidth: 1 },
            text: { fill: colors.gray[100] },
          },
        },
        legends: { text: { fill: colors.gray[100] } },
        tooltip: { container: { color: colors.primary[500] } },
      }}
      keys={["stock"]}
      indexBy="category"
      margin={chartMargin}
      padding={0.3}
      valueScale={{ type: "linear" }}
      indexScale={{ type: "band", round: true }}
      colors={(d) => getColor(d.data.stock)}
      borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: isMobile ? -45 : 0, // Tilt text on mobile to prevent clashing
        legend: isDashboard ? undefined : "Top Products",
        legendPosition: "middle",
        legendOffset: isMobile ? 65 : 40, // Adjust legend distance dynamically
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: isDashboard ? undefined : "Units Sold",
        legendPosition: "middle",
        legendOffset: -50,
      }}
      enableLabel={false}
      legends={[]}
      role="application"
      ariaLabel="Top products bar chart"
      barAriaLabel={(e) =>
        `${e.id}: ${e.formattedValue} in category: ${e.indexValue}`
      }
    />
  );
};

export default InventoryOut;
